/**
 * orgCsv — pure CSV helpers for the Org Chart feature (no Office.js, no DOM),
 * so the parse/serialise maths can be unit-tested directly.
 *
 * The format is the one the panel documents: three columns — `Name`, `Title`,
 * `Reports To` — where `Reports To` is the MANAGER'S NAME (not an id). Parsing
 * resolves those names back to `parentId`s after every row has an id.
 */

import type { OrgPerson } from "../../../types";

/** Column header written to (and expected from) the CSV. */
export const CSV_HEADERS = ["Name", "Title", "Reports To"] as const;

/** Sample rows shipped in the downloadable template. */
const TEMPLATE_ROWS: ReadonlyArray<readonly [string, string, string]> = [
  ["Arjun Mehta", "CEO", ""],
  ["Sara Lin", "COO", "Arjun Mehta"],
  ["Jane Smith", "VP Engineering", "Sara Lin"],
];

/** Quote a field for CSV output only when it contains a comma, quote or newline. */
function escapeCsv(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Build the downloadable CSV template (header + a few sample rows). */
export function buildCsvTemplate(): string {
  const lines = [CSV_HEADERS.join(",")];
  for (const row of TEMPLATE_ROWS) {
    lines.push(row.map(escapeCsv).join(","));
  }
  return lines.join("\r\n");
}

/**
 * Split one CSV line into fields, honouring double-quoted values (which may
 * contain commas and escaped `""` quotes). Kept deliberately small — the org
 * CSV has three simple columns, not arbitrary spreadsheet exports.
 */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      fields.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields.map((f) => f.trim());
}

/** True when a row looks like the header (first cell is "name", any case). */
function isHeaderRow(fields: readonly string[]): boolean {
  return (fields[0] ?? "").toLowerCase() === "name";
}

/**
 * Parse org-chart CSV text into a connected `OrgPerson[]`.
 *
 * Names are assigned sequential ids; `Reports To` is matched to a manager by
 * name (case-insensitive, first match). Unknown or self-referential managers
 * become top-level (`parentId: null`) so the tree always stays valid.
 *
 * @throws if no data rows with a name are found.
 */
export function parseOrgCsv(text: string): OrgPerson[] {
  const rows = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map(splitCsvLine);

  const dataRows = rows.filter((r, i) => !(i === 0 && isHeaderRow(r)));

  const people: OrgPerson[] = [];
  // Remember the manager name per new person so we can resolve ids in a 2nd pass.
  const managerNames: Array<string> = [];
  for (const fields of dataRows) {
    const name = (fields[0] ?? "").trim();
    if (!name) continue; // skip blank/malformed rows rather than fail the whole import
    people.push({
      id: people.length + 1,
      name,
      title: (fields[1] ?? "").trim(),
      parentId: null,
    });
    managerNames.push((fields[2] ?? "").trim());
  }

  if (people.length === 0) {
    throw new Error("No people found in the CSV. Expected columns: Name, Title, Reports To.");
  }

  const byName = new Map<string, number>();
  for (const p of people) {
    const key = p.name.toLowerCase();
    if (!byName.has(key)) byName.set(key, p.id);
  }

  people.forEach((person, i) => {
    const manager = managerNames[i]?.toLowerCase();
    if (!manager) return;
    const managerId = byName.get(manager);
    if (managerId != null && managerId !== person.id) {
      person.parentId = managerId;
    }
  });

  return people;
}
